import { requireData, requireSuccess } from "../../lib/appError";
import type { Database } from "../../lib/database.types";
import { supabase } from "../../lib/supabaseClient";
import type {
  AgentConversation,
  AgentMessage,
  AgentTurn,
} from "./agentTranscript";

// The row types and `appliedChangesOf` live in agentTranscript so the pure
// chat logic can be tested without the Supabase client.
export {
  appliedChangesOf,
  type AgentConversation,
  type AgentMessage,
  type AgentTurn,
  type AppliedChange,
} from "./agentTranscript";

export type ConversationHandlers = {
  onMessage: (message: AgentMessage) => void;
  onTurn: (turn: AgentTurn) => void;
  // Fires each time the channel (re)subscribes, including the first. Realtime
  // does not replay what was missed while disconnected, so the caller
  // re-fetches here.
  onSubscribed: () => void;
};

export const agentRepository = {
  // Conversations for the signed-in owner, newest first. Building "many
  // conversations per site" later only widens this query.
  async listConversations(siteId: string): Promise<AgentConversation[]> {
    const { data, error } = await supabase
      .from("agent_conversations")
      .select("*")
      .eq("site_id", siteId)
      .order("created_at", { ascending: false });
    return requireData(data, error, "Could not load the conversations.");
  },

  async createConversation(
    siteId: string,
    userId: string,
  ): Promise<AgentConversation> {
    const { data, error } = await supabase
      .from("agent_conversations")
      .insert({ site_id: siteId, user_id: userId })
      .select()
      .single();
    return requireData(data, error, "Could not start the conversation.");
  },

  async listMessages(conversationId: string): Promise<AgentMessage[]> {
    const { data, error } = await supabase
      .from("agent_messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    return requireData(data, error, "Could not load the conversation.");
  },

  // The conversation's newest turn, or null before the first message.
  async latestTurn(conversationId: string): Promise<AgentTurn | null> {
    const { data, error } = await supabase
      .from("agent_turns")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    requireSuccess(error, "Could not load Ducky's status.");
    return data;
  },

  // Asks the running worker to stop; it sees the flag on its next heartbeat.
  // A no-op for a turn that already finished.
  async cancelTurn(turnId: string): Promise<void> {
    const { error } = await supabase.rpc("request_agent_turn_cancel", {
      p_turn_id: turnId,
    });
    requireSuccess(error, "Could not stop Ducky.");
  },

  // Live rows for one conversation over Postgres Changes. RLS filters what
  // each subscriber receives (owner-only select), and the `conversation_id`
  // filter keeps the stream to this chat. Returns the unsubscribe function.
  subscribeToConversation(
    conversationId: string,
    handlers: ConversationHandlers,
  ): () => void {
    const filter = `conversation_id=eq.${conversationId}`;
    const onTurn = (payload: { new: unknown }) =>
      handlers.onTurn(payload.new as AgentTurn);
    const channel = supabase
      .channel(`agent-conversation:${conversationId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "agent_messages", filter },
        (payload) => handlers.onMessage(payload.new as AgentMessage),
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "agent_turns", filter },
        onTurn,
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "agent_turns", filter },
        onTurn,
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") handlers.onSubscribed();
      });
    return () => {
      void supabase.removeChannel(channel);
    };
  },

  async createMessage(
    conversationId: string,
    values: Pick<Database["public"]["Tables"]["agent_messages"]["Insert"], "role" | "content"> &
      { tool_calls?: AgentMessage["tool_calls"] },
  ): Promise<AgentMessage> {
    const { data, error } = await supabase
      .from("agent_messages")
      .insert({
        conversation_id: conversationId,
        role: values.role,
        content: values.content,
        ...(values.tool_calls === undefined
          ? {}
          : { tool_calls: values.tool_calls }),
      })
      .select()
      .single();
    return requireData(data, error, "Could not save the message.");
  },
};
